import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { BadgeEuro, ChevronLeft, ChevronRight, FolderOpen, Undo2 } from "lucide-react";
import { KpiCard } from "@/components/KpiCard";
import { EmptyState } from "@/components/EmptyState";
import { ErrorState } from "@/components/ErrorState";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { formatDate, formatMonth } from "@/lib/dates";
import { getQuotaRete, isAccessoSospeso } from "@/api/rete";
import type { QuotaReteResponse, StatoQuotaRete } from "@/types/rete";
import { AccessoSospeso } from "./AccessoSospeso";

function euro(n: number): string {
  return `€${n.toLocaleString("it-IT", { minimumFractionDigits: 2 })}`;
}

const STATO: Record<StatoQuotaRete, string> = {
  maturata: "Maturata",
  azzerata: "Azzerata",
  stornata: "Stornata",
};

/** Mese dall'URL (`?anno=&mese=`), altrimenti quello corrente. */
function meseDaUrl(sp: URLSearchParams): { anno: number; mese: number } {
  const oggi = new Date();
  const anno = Number(sp.get("anno")) || oggi.getFullYear();
  const mese = Number(sp.get("mese")) || oggi.getMonth() + 1;
  return { anno, mese: Math.min(12, Math.max(1, mese)) };
}

function spostaMese(anno: number, mese: number, delta: number): { anno: number; mese: number } {
  const d = new Date(anno, mese - 1 + delta, 1);
  return { anno: d.getFullYear(), mese: d.getMonth() + 1 };
}

export function QuotaRete() {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const { anno, mese } = meseDaUrl(sp);
  const [data, setData] = useState<QuotaReteResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sospeso, setSospeso] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getQuotaRete(anno, mese)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => {
        if (cancelled) return;
        if (isAccessoSospeso(e)) setSospeso(true);
        else setError("Impossibile caricare la quota.");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [anno, mese]);

  function vaiAlMese(delta: number) {
    const m = spostaMese(anno, mese, delta);
    setSp({ anno: String(m.anno), mese: String(m.mese) });
  }

  if (sospeso) return <AccessoSospeso />;

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 bg-muted rounded-lg animate-pulse" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-card rounded-xl border border-border p-5 space-y-3">
              <div className="h-3 w-24 bg-muted rounded animate-pulse" />
              <div className="h-8 w-16 bg-muted rounded animate-pulse" />
            </div>
          ))}
        </div>
        <div className="bg-card rounded-xl border border-border p-5 space-y-3">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-12 bg-muted rounded-lg animate-pulse" />
          ))}
        </div>
      </div>
    );
  }
  if (error || !data) return <ErrorState message={error ?? "Impossibile caricare la quota."} />;

  const meseMinuscolo = formatMonth(data.month);
  const meseLeggibile = meseMinuscolo.charAt(0).toUpperCase() + meseMinuscolo.slice(1);
  const bottoneMese =
    "min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg border border-border bg-card text-muted-foreground hover:text-text transition-colors";

  function apriPratica(opportunityId: string) {
    navigate(`/rete/pratiche/${encodeURIComponent(opportunityId)}`);
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h2 className="text-xl font-bold text-text">La tua quota</h2>
        <div className="flex items-center gap-1">
          <button type="button" aria-label="Mese prima" onClick={() => vaiAlMese(-1)} className={bottoneMese}>
            <ChevronLeft size={18} />
          </button>
          <span className="min-w-[9rem] text-center text-sm font-medium text-text">{meseLeggibile}</span>
          <button type="button" aria-label="Mese dopo" onClick={() => vaiAlMese(1)} className={bottoneMese}>
            <ChevronRight size={18} />
          </button>
        </div>
      </div>

      {data.righe.length === 0 ? (
        <EmptyState
          icon={<BadgeEuro size={26} className="text-brand" />}
          title="Nessuna quota nel mese"
          description="La quota matura quando una pratica delle tue agenzie viene accettata."
        />
      ) : (
        <>
          {/* L'importo unitario arriva dal backend: qui non si moltiplica nulla. */}
          <p className="text-sm text-muted-foreground">
            Importo a pratica: {euro(data.importo_pratica)}
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KpiCard
              label="Quota del mese"
              value={euro(data.totale)}
              icon={<BadgeEuro size={16} />}
              variant="hero"
            />
            <KpiCard label="Pratiche" value={data.n_pratiche} icon={<FolderOpen size={16} />} />
            {data.da_recuperare > 0 && (
              <KpiCard
                label="Da recuperare"
                value={euro(data.da_recuperare)}
                icon={<Undo2 size={16} />}
                accentClass="border-l-amber-400"
              />
            )}
          </div>

          <div className="bg-card rounded-xl border border-border overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Pratica</TableHead>
                    <TableHead className="hidden md:table-cell">Agenzia</TableHead>
                    <TableHead className="hidden md:table-cell">Competenza</TableHead>
                    <TableHead className="text-right">Importo</TableHead>
                    <TableHead>Stato</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.righe.map((r) => (
                    <TableRow
                      key={r.opportunity_id}
                      {...(r.in_carico
                        ? {
                            role: "button",
                            tabIndex: 0,
                            onClick: () => apriPratica(r.opportunity_id),
                            onKeyDown: (e: React.KeyboardEvent) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                apriPratica(r.opportunity_id);
                              }
                            },
                          }
                        : {})}
                      // Una riga non maturata è barrata E porta il suo stato scritto:
                      // il segno da solo non basta a chi non distingue il grigio.
                      // Un'agenzia non più in carico non è cliccabile: il suo dettaglio
                      // darebbe un 404 (la spec riserva quel percorso alle agenzie attuali).
                      className={`[&_td]:py-3${r.in_carico ? " cursor-pointer" : " hover:bg-transparent"}${
                        r.stato === "maturata" ? "" : " line-through text-muted-foreground"
                      }`}
                    >
                      <TableCell className="font-medium text-text">{r.pratica_nome ?? "—"}</TableCell>
                      <TableCell className="hidden md:table-cell text-muted-foreground">
                        {r.agenzia_nome}
                      </TableCell>
                      <TableCell className="hidden md:table-cell tabular-nums">
                        {formatDate(r.competenza)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{euro(r.importo)}</TableCell>
                      <TableCell>{STATO[r.stato]}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
